function animateCartRowRemove(row) {
    return new Promise((resolve) => {
        if (!row) {
            resolve();
            return;
        }

        const height = row.getBoundingClientRect().height;
        row.style.overflow = 'hidden';
        row.style.height = `${height}px`;
        row.style.opacity = '1';
        row.style.transition = 'opacity 0.22s ease, height 0.28s ease, margin 0.28s ease, padding 0.28s ease';

        requestAnimationFrame(() => {
            row.style.opacity = '0';
            row.style.height = '0px';
            row.style.marginTop = '0px';
            row.style.marginBottom = '0px';
            row.style.paddingTop = '0px';
            row.style.paddingBottom = '0px';
        });

        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
        };

        row.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, 320);
    });
}

class CartPopup extends HTMLElement {
    constructor() {
        super();
        this.isUpdating = false;
        this.handleItemAdded = this.handleItemAdded.bind(this);
    }

    connectedCallback() {
        if (this.isInitialized) return;
        this.isInitialized = true;

        this.container = this.querySelector("[data-popup-container]");
        this.currentAddedVariantId = null;
        this.currentHandle = null;

        if (!this.container) {
            console.error("CartPopup: no [data-popup-container]");
            return;
        }
        
        document.addEventListener("cart:item-added", this.handleItemAdded);
        this.addEventListener("click", this.handleClicks.bind(this));
    }

    disconnectedCallback() {
        document.removeEventListener("cart:item-added", this.handleItemAdded);
    }

    handleItemAdded(e) {
        this.currentAddedVariantId = e.detail.addedVariantId;
        this.currentHandle = e.detail.handle;
        this.loadPopupContent(this.currentHandle, this.currentAddedVariantId);
    }

    handleClicks(e) {
        if (this.isUpdating) return;

        if (e.target.hasAttribute("data-popup-close") || e.target === this || e.target.closest('[data-popup-close]')) {
            this.close();
            return;
        }

        const actionBtn = e.target.closest('[data-action="plus"], [data-action="minus"]');
        if (actionBtn) {
            const line = actionBtn.dataset.line;
            let quantity = parseInt(actionBtn.dataset.quantity);
            
            if (actionBtn.dataset.action === 'plus') {
                const maxQuantity = parseInt(actionBtn.dataset.maxQuantity) || 9999;
                if (quantity >= maxQuantity) return; 
                quantity += 1;
            } else {
                quantity -= 1;
            }
            
            this.updateItem(line, quantity, actionBtn);
            return;
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
            this.updateItem(removeBtn.dataset.line, 0, removeBtn).then(() => {
                this.close(); 
            });
            return;
        }

        const variantOption = e.target.closest('[data-dropdown-option]');
        if (variantOption && variantOption.hasAttribute('data-variant-id') && !variantOption.disabled) {
            if (!variantOption.classList.contains('active')) {
                this.changeCartVariant(
                    variantOption.dataset.line,
                    1, 
                    variantOption.dataset.variantId
                );
            }
        }
    }

    openPopup() {
        this.style.display = "flex";
        document.body.style.overflow = "hidden"; 
    }

    close() {
        this.style.display = "none";
        document.body.style.overflow = ""; 
    }

    async loadPopupContent(handle, variantId) {
        this.openPopup();
        this.isUpdating = true;

        try {
            const url = `${window.Shopify.routes.root}products/${handle}?variant=${variantId}&section_id=ajax-cart-popup`;
            const response = await fetch(url);
            
            if (!response.ok) throw new Error("Error loading section");
            const htmlText = await response.text();
            
            this.container.innerHTML = htmlText;
            this.initializeSwiper();

        } catch (error) {
            console.error("Popup load error:", error);
        } finally {
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
            this.isUpdating = false;
        }
    }

    async updateItem(line, quantity, triggerEl) {
        if (this.isUpdating) return;
        this.isUpdating = true;
        try {
            if (quantity <= 0) {
                await animateCartRowRemove(triggerEl?.closest('li'));
            }

            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line, quantity })
            });
            
            if (quantity > 0) {
                await this.loadPopupContent(this.currentHandle, this.currentAddedVariantId);
            } 
        } catch (error) {
            console.error('Error updating popup cart:', error);
        } finally {
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
            this.isUpdating = false;
        }
    }

    async changeCartVariant(line, quantity, newVariantId) {
        if (this.isUpdating) return;
        this.isUpdating = true;
        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line, quantity: 0 })
            });
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: newVariantId, quantity })
            });
            
            this.currentAddedVariantId = newVariantId;
            await this.loadPopupContent(this.currentHandle, newVariantId);
        } catch (error) {
            console.error('Error changing popup variant:', error);
        } finally {
            this.isUpdating = false;
        }
    }

    initializeSwiper() {
        if (typeof Swiper === "undefined") return;

        const desktopEl = this.querySelector(".swiper-product-to-cart-desktop");
        if (desktopEl) {
            new Swiper(desktopEl, {
                navigation: {
                    nextEl: '.swiper-button-next-desktop',
                    prevEl: '.swiper-button-prev-desktop',
                },
                slidesPerView: 'auto',
                spaceBetween: 1,
                loop: false,
            });
        }

        const mobileEl = this.querySelector(".swiper-product-to-cart-mobile");
        if (mobileEl) {
            new Swiper(mobileEl, {
                navigation: {
                    nextEl: '.swiper-button-next-mobile',
                    prevEl: '.swiper-button-prev-mobile',
                },
                slidesPerView: 'auto',
                spaceBetween: 8,
                loop: false,
            });
        }
    }
}

if (!customElements.get("cart-popup")) {
    customElements.define("cart-popup", CartPopup);
}